import * as React from "react"
import {
  MerlinStatus,
  MerlinSourceEventResourceType,
} from "@gatsbyjs/graphql/types"
import { CheckIcon } from "gatsby-interface"
import { FiXOctagon, FiSlash } from "react-icons/fi"
import { Spinner } from "gatsby-ui/src/components"
import { clsx } from "clsx"
import * as styles from "./styles.css"
import { getContextuallyFormattedDate } from "./helpers"
import {
  useMerlinStatusForResourceQuery,
  MerlinStatusForResourceDocument,
  MerlinStatusForResourceQuery,
  MerlinStatusForResourceQueryVariables,
  useMerlinStatusChangedSubscription,
} from "./queries.generated"

export const statusLabels = {
  [MerlinStatus.Cancelled]: `Cancelled`,
  [MerlinStatus.Success]: `Updated`,
  [MerlinStatus.Idle]: `Pending`,
  [MerlinStatus.InProgress]: `Sourcing data`,
  [MerlinStatus.Failed]: `Error - please re-source data`,
}

export const statusIcons = {
  [MerlinStatus.Cancelled]: FiSlash,
  [MerlinStatus.Success]: CheckIcon,
  [MerlinStatus.Idle]: null,
  [MerlinStatus.InProgress]: Spinner,
  [MerlinStatus.Failed]: FiXOctagon,
}

export const statusStyles = {
  [MerlinStatus.Cancelled]: null,
  [MerlinStatus.Success]: null,
  [MerlinStatus.Idle]: null,
  [MerlinStatus.InProgress]: null,
  [MerlinStatus.Failed]: styles.failedColor,
}

export type SiteDataLayerStatusProps = {
  siteId: string
  isEnabled: boolean
}

export function SiteDataLayerStatus({
  siteId,
  isEnabled = false,
}: SiteDataLayerStatusProps) {
  const variables = {
    id: siteId,
    resourceType: MerlinSourceEventResourceType.Site,
  }

  const sourcingStatus = useMerlinStatusForResourceQuery({
    variables,
    skip: !isEnabled,
    pollInterval: 5000,
  })

  useMerlinStatusChangedSubscription({
    variables: {
      resourceId: siteId,
      resourceType: MerlinSourceEventResourceType.Site,
    },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const queryInfo = {
        query: MerlinStatusForResourceDocument,
        variables,
      }

      try {
        const currentData = client.readQuery<
          MerlinStatusForResourceQuery,
          MerlinStatusForResourceQueryVariables
        >(queryInfo)

        const newStatus = subscriptionData.data?.merlinStatusChanged

        if (newStatus) {
          client.writeQuery<
            MerlinStatusForResourceQuery,
            MerlinStatusForResourceQueryVariables
          >({
            ...queryInfo,
            data: {
              merlinStatusForResource: {
                ...currentData?.merlinStatusForResource,
                ...newStatus,
              },
            },
          })
        }
      } catch (e) {
        console.error(e)
      }
    },
  })

  const status = sourcingStatus.data?.merlinStatusForResource?.status
  const updatedAt = sourcingStatus.data?.merlinStatusForResource?.updatedAt

  if (!status) {
    return null
  }

  const label = statusLabels?.[status]
  const Icon = statusIcons?.[status]
  const style = statusStyles?.[status]

  const isSuccess = status === MerlinStatus.Success

  return (
    <div className={styles.siteStatus}>
      {Icon && <Icon className={clsx(styles.siteStatusIcon, style)} />}
      {label && (
        <span className={clsx(style)}>
          {label}{" "}
          {updatedAt && isSuccess && getContextuallyFormattedDate(updatedAt)}
        </span>
      )}
    </div>
  )
}
